import Alpine, { AlpineComponent } from "alpinejs";
import { Basket, BasketService, CreateOrderParameters, GoogleService, OrderService } from "../api";
import { useTranslations } from "../hooks/useTranslations.ts";
import { Translations } from "../types/Translations.ts";
import { SubmissionError, toSubmissionErrors } from "../utils/apiErrors.ts";
import { getOrderTotal } from "../utils/getOrderTotal.ts";

type Checkout = AlpineComponent<{
	invoiceAddress: Address;
	defaultDeliveryAddress: Address;
	deliveryAddressToAdd: Address;
	alternativeDeliveryAddress: Address | undefined;
	usingAlternativeDeliveryAddress: boolean;
	partialDeliveryAccepted: boolean;
	pickingBetweenDeliveryAddresses: boolean;
	pickingAlternativeDeliveryAddress: boolean;
	pickingDefaultDeliveryAddress: boolean;
	addingOrEditingAlternativeDeliveryAddress: boolean;
	requestedDeliveryDate: string | null;
	message: string;
	projectName: string;
	translations: Translations;
	submissionErrors: SubmissionError[];
	isLoading: boolean;
	countries: Map<string, string>;
	saveDeliveryAddress: (address: Address, isAdded: boolean) => Promise<void>;
	pickBetweenDeliveryAddresses: () => void;
	updateBasket: () => Promise<void>;
	submitOrder: () => Promise<void>;
	beginEditingAlternativeDeliveryAddress: () => void;
	salesRepresentative: string | undefined;
}>;

interface Address {
	addressRow: string;
	city: string;
	postalCode: string;
	companyName?: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	countryCode?: string;
}

interface FormData {
	invoiceAddress: Address;
	defaultDeliveryAddress: Address;
	alternativeDeliveryAddress?: Address;
	usingAlternativeDeliveryAddress: boolean;
	countries: Map<string, string>;
	salesRepresentative?: string;
}

export default (initialBasket: Basket, initialForm: FormData): Checkout => {
	const t = useTranslations();
	Alpine.store("basket", initialBasket);
	return {
		invoiceAddress: initialForm.invoiceAddress,
		defaultDeliveryAddress: initialForm.defaultDeliveryAddress,
		addingOrEditingAlternativeDeliveryAddress: false,
		alternativeDeliveryAddress: {
			addressRow: initialForm.alternativeDeliveryAddress?.addressRow || initialForm.defaultDeliveryAddress.addressRow,
			city: initialForm.alternativeDeliveryAddress?.city || initialForm.defaultDeliveryAddress.city,
			postalCode: initialForm.alternativeDeliveryAddress?.postalCode || initialForm.defaultDeliveryAddress.postalCode,
			companyName:
				initialForm.alternativeDeliveryAddress?.companyName || initialForm.defaultDeliveryAddress.companyName,
			firstName: initialForm.alternativeDeliveryAddress?.firstName || initialForm.defaultDeliveryAddress.firstName,
			lastName: initialForm.alternativeDeliveryAddress?.lastName || initialForm.defaultDeliveryAddress.lastName,
			email: initialForm.alternativeDeliveryAddress?.email || initialForm.defaultDeliveryAddress.email,
			phone: initialForm.alternativeDeliveryAddress?.phone || initialForm.defaultDeliveryAddress.phone,
			countryCode:
				initialForm.alternativeDeliveryAddress?.countryCode || initialForm.defaultDeliveryAddress.countryCode,
		},
		deliveryAddressToAdd: {
			addressRow: initialForm.alternativeDeliveryAddress?.addressRow || initialForm.defaultDeliveryAddress.addressRow,
			city: initialForm.alternativeDeliveryAddress?.city || initialForm.defaultDeliveryAddress.city,
			postalCode: initialForm.alternativeDeliveryAddress?.postalCode || initialForm.defaultDeliveryAddress.postalCode,
			companyName:
				initialForm.alternativeDeliveryAddress?.companyName || initialForm.defaultDeliveryAddress.companyName,
			firstName: initialForm.alternativeDeliveryAddress?.firstName || initialForm.defaultDeliveryAddress.firstName,
			lastName: initialForm.alternativeDeliveryAddress?.lastName || initialForm.defaultDeliveryAddress.lastName,
			email: initialForm.alternativeDeliveryAddress?.email || initialForm.defaultDeliveryAddress.email,
			phone: initialForm.alternativeDeliveryAddress?.phone || initialForm.defaultDeliveryAddress.phone,
			countryCode:
				initialForm.alternativeDeliveryAddress?.countryCode || initialForm.defaultDeliveryAddress.countryCode,
		},
		message: initialBasket?.comment,
		projectName: initialBasket?.projectName,
		usingAlternativeDeliveryAddress: initialBasket?.usingAlternativeDeliveryAddress,
		partialDeliveryAccepted: initialBasket?.partialDeliveryAccepted,
		requestedDeliveryDate: initialBasket?.requestedDeliveryDate || "",
		pickingBetweenDeliveryAddresses: false,
		submissionErrors: [],
		isLoading: false,
		pickingAlternativeDeliveryAddress: initialBasket?.usingAlternativeDeliveryAddress,
		pickingDefaultDeliveryAddress: !initialBasket?.usingAlternativeDeliveryAddress,
		translations: t,
		countries: initialForm.countries,
		salesRepresentative: initialForm.salesRepresentative,
		async init() {
			await getOrderTotal();
			const beginCheckout = await GoogleService.beginCheckout();
			window.dataLayer.push({
				ecommerce: null,
			});
			window.dataLayer.push({
				event: "begin_checkout",
				ecommerce: beginCheckout.data,
			});
		},
		async saveDeliveryAddress(address: Address, usingAlternativeDeliveryAddress: boolean) {
			const form = document.getElementById("checkoutForm") as HTMLFormElement;
			const invoicePhoneInput = document.getElementById("invoicePhone") as HTMLInputElement;
			invoicePhoneInput.disabled = true;
			if (!form.checkValidity()) {
				form.reportValidity();
				invoicePhoneInput.disabled = false;
				return;
			}
			invoicePhoneInput.disabled = false;
			await OrderService.updateDeliveryInformation({
				body: {
					company: address?.companyName || "",
					firstName: address?.firstName || "",
					lastName: address?.lastName || "",
					email: address?.email || "",
					phone: address?.phone || "",
					address: address?.addressRow || "",
					city: address?.city || "",
					postalCode: address?.postalCode || "",
					usingAlternativeDeliveryAddress: usingAlternativeDeliveryAddress,
					countryCode: address?.countryCode || "",
				},
			}).then(async () => {
				this.addingOrEditingAlternativeDeliveryAddress = false;
				this.usingAlternativeDeliveryAddress = usingAlternativeDeliveryAddress;
				this.pickingAlternativeDeliveryAddress = usingAlternativeDeliveryAddress;
				this.pickingDefaultDeliveryAddress = !usingAlternativeDeliveryAddress;
				if (usingAlternativeDeliveryAddress) {
					this.alternativeDeliveryAddress = {
						addressRow: address.addressRow,
						city: address.city,
						postalCode: address.postalCode,
						companyName: address.companyName,
						firstName: address.firstName,
						lastName: address.lastName,
						email: address.email,
						phone: address.phone,
						countryCode: address.countryCode,
					};
				}
				await this.updateBasket();
			});
		},
		pickBetweenDeliveryAddresses() {
			if (this.pickingDefaultDeliveryAddress) {
				this.saveDeliveryAddress(this.defaultDeliveryAddress, false);
			}
			this.pickingBetweenDeliveryAddresses = false;
		},
		beginEditingAlternativeDeliveryAddress() {
			if (this.usingAlternativeDeliveryAddress) {
				this.deliveryAddressToAdd = {
					addressRow: this.alternativeDeliveryAddress?.addressRow || "",
					city: this.alternativeDeliveryAddress?.city || "",
					postalCode: this.alternativeDeliveryAddress?.postalCode || "",
					companyName: this.alternativeDeliveryAddress?.companyName || "",
					firstName: this.alternativeDeliveryAddress?.firstName || "",
					lastName: this.alternativeDeliveryAddress?.lastName || "",
					email: this.alternativeDeliveryAddress?.email || "",
					phone: this.alternativeDeliveryAddress?.phone || "",
					countryCode: this.alternativeDeliveryAddress?.countryCode || "",
				};
			} else {
				this.deliveryAddressToAdd = {
					addressRow: this.defaultDeliveryAddress.addressRow,
					city: this.defaultDeliveryAddress.city,
					postalCode: this.defaultDeliveryAddress.postalCode,
					companyName: this.defaultDeliveryAddress.companyName,
					firstName: this.defaultDeliveryAddress.firstName,
					lastName: this.defaultDeliveryAddress.lastName,
					email: this.defaultDeliveryAddress.email,
					phone: this.defaultDeliveryAddress.phone,
					countryCode: this.defaultDeliveryAddress.countryCode,
				};
			}
			this.addingOrEditingAlternativeDeliveryAddress = true;
		},
		async updateBasket() {
			await BasketService.updateBasket({
				body: {
					requestedDeliveryDate: new Date(this.requestedDeliveryDate || ""),
					partialDeliveryAccepted: this.partialDeliveryAccepted,
					usingAlternativeDeliveryAddress: this.usingAlternativeDeliveryAddress,
					comment: this.message,
					projectName: this.projectName,
					countryCode: this.usingAlternativeDeliveryAddress
						? this.alternativeDeliveryAddress?.countryCode || ""
						: this.defaultDeliveryAddress.countryCode || "",
					deliveryAddressCompanyName: this.usingAlternativeDeliveryAddress
						? this.alternativeDeliveryAddress?.companyName || ""
						: this.defaultDeliveryAddress.companyName || "",
					invoiceAddressPhone: this.invoiceAddress.phone || "",
				},
			}).then(async (response) => {
				this.$store.basket = response.data;
				await getOrderTotal();
			});
		},
		async submitOrder() {
			const form = document.getElementById("checkoutForm") as HTMLFormElement;

			if (!form.checkValidity()) {
				const invalidInDelivery = this.$refs.deliverySection?.querySelector(":invalid") as
					| (HTMLElement & { reportValidity?: () => boolean })
					| null;

				if (invalidInDelivery) {
					this.addingOrEditingAlternativeDeliveryAddress = true;

					this.$nextTick(() => {
						invalidInDelivery.focus?.();
						invalidInDelivery.reportValidity?.();
					});
					return;
				}

				const firstInvalid = form.querySelector(":invalid") as
					| (HTMLElement & { reportValidity?: () => boolean })
					| null;
				firstInvalid?.focus?.();
				firstInvalid?.reportValidity?.();
				return;
			}

			const orderData: CreateOrderParameters = {
				requestedDeliveryDate: new Date(this.requestedDeliveryDate || ""),
				partialDeliveryAccepted: this.partialDeliveryAccepted,
				projectName: this.projectName,
				message: this.message,
				contentId: Number(document.documentElement?.dataset?.contentId),
				usingAlternativeDeliveryAddress: this.usingAlternativeDeliveryAddress,
				invoiceAddressPhoneNumber: this.invoiceAddress.phone || "",
			};

			this.isLoading = true;
			this.submissionErrors = [];

			try {
				const { data, error } = await OrderService.create({ body: orderData });

				if (!error && data) {
					location.href = data.orderConfirmationPageUrl;
					return; // keep the loading state while the browser navigates
				}

				this.submissionErrors = toSubmissionErrors(error);
			} catch (error) {
				console.error("Error submitting order:", error);
				this.submissionErrors = toSubmissionErrors(error);
			}

			setTimeout(() => {
				this.isLoading = false;
			}, 3000);
		},
	};
};
